import { message } from 'antd'
import React from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'

import {
  updateCurrentAddCartCount,
  updateCurrentAddCartSkuIds
} from '@/two/src/store/slice/appSlice'
import heWinPixel from '@api/hernest/winPixel_m.bak'
import { Has, PageData } from '@api/home/detail/types'
import { addCart, buyNow } from '@api/home/shopCart/action'
import { routesPath } from '@api/home/utils'
import useGlobalEvent, { dispatchGlobalEvent } from '@hooks/useGlobalEvent'
import { isHomeProject, loTrack } from '@utils'
import { Type } from '@utils/tool'
import winPixel, { pathnameToPage } from '@utils/winPixel'

import {
  setList,
  setShopGroupList,
  setShopListTotalInfo
} from '../../shopCart/cartSlice'
import { ctx } from '../useDetail'
import { useCartListDrawer } from './useCartListDrawer'

export const CartDrawer = React.lazy(() => {
  return import(
    `@/${process.env.template}/src/pages/ShopCart/components/CartList/sideCartlist`
  )
})

// export const CartDrawer = () => <></>

export type Props = {
  data: {
    productsId: string
    skuId: string | number
  } & PageData['productInfo']
  isOnlyOne?
  AddCart?
  noAbtest?
  children?
  reqParams?
  currentUrl?
  forward?
}

export const numOptions = (num: number) =>
  Array.from({ length: Number(num) }, (_, index) => index + 1).map((index) => {
    return {
      value: index,
      label: index
    }
  })

/**
 * @data.productsStatus 1 在售 2下架 3预售
 */

export const getMarketingInfo = ({
  stocks,
  productsStatus,
  isOutStockCanBuy
}) => {
  const isPreOrder =
    (Number(stocks) <= 0 && isOutStockCanBuy === Has.Yes) ||
    Number(productsStatus) === 3

  const isSoldOut =
    (Number(stocks) <= 0 && isOutStockCanBuy === Has.No) ||
    Number(productsStatus) === 2

  return { isPreOrder, isSoldOut }
}

export const useAdd = (
  siteName: 'yitahome' | 'hernest' = 'hernest',
  terminal?: 'hepc' | 'hem' | 'homepc' | 'homem'
) => {
  const handler = useCartListDrawer({
    title: `My Cart`,
    isOpenFetch: true,
    noGroup: terminal === 'hem' || terminal === 'hepc'
  })

  const onAdd = (
    props: {
      productsId
      skuId
      quantity?
      page?: string
      module?: string
      params?: { [key: string]: any }
    } & PageData['productInfo']
  ) => {
    const { productsId, skuId, quantity = 1, page, module, params = {} } = props

    switch (siteName) {
      case 'yitahome':
        winPixel.addToCart(
          'ProductDetail',
          props,
          quantity,
          [
            'AW-557240052/OBYOCNTHscsYEPSd24kC',
            'AW-11323451663/gpMZCLSVq9wYEI_SuJcq',
            'AW-11327320617/QCYvCN78qPIYEKnkpJkq'
          ],
          {
            page,
            module
          }
        )
        break

      case 'hernest':
        heWinPixel.addToCart(page ? page : 'ProductDetail', props, quantity, {
          page,
          module
        })
        break
    }

    let obj = {
      products_id: productsId,
      sku_id: skuId,
      quantity: quantity,
      show_group: '1',
      ...params
    }

    if (terminal === 'hem' || terminal === 'hepc') obj.show_group = '0'

    return addCart(obj)
      .then((res) => {
        // 当参数带了 show_group 显示分组数据时，调用 setShopGroupList 更新购物车数据，反之调用 setList
        if (obj.show_group === '1') {
          handler.dispatch(setShopGroupList(res))
        } else {
          handler.dispatch(setList(res))
        }
        handler.dispatch(setShopListTotalInfo(res))

        // message.success('SUCCESS')
      })
      .finally(() => {
        handler.setOpen(true)
      })
  }

  return { onAdd, handler }
}

export const useAddCart = (
  props: Props,
  siteName: 'yitahome' | 'hernest' = 'yitahome',
  terminal?: 'hepc' | 'hem' | 'homepc' | 'homem',
  orgFrom?: 'detailMain'
) => {
  const { data, reqParams = {}, currentUrl } = props

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const [loading, useLoading] = React.useState(false)

  const ref = React.useRef<{ value: number }>({ value: 1 })

  const handler = useCartListDrawer({
    title: `My Cart`,
    isOpenFetch: true,
    noGroup: terminal === 'hem' || terminal === 'hepc'
  })

  let { isPreOrder, isSoldOut } = getMarketingInfo({ ...data })

  const onClick = (
    props = {} as { productsId; skuId; quantity; showSkuList?; noDrawer? },
    params: { [key: string]: any } = {},
    callback?: Function,
    winPixelParams: { [key: string]: any } = {}
  ) => {
    const {
      productsId = data.productsId,
      skuId = data.skuId,
      quantity = ref.current.value,
      showSkuList,
      noDrawer = false
    } = props

    const pageName = pathnameToPage(currentUrl || location.pathname)

    const winParams = {
      page: pageName,
      module: 'FirstScreen',
      ...winPixelParams
    }

    useLoading(true)

    const obj = {
      products_id: productsId,
      sku_id: skuId,
      quantity: quantity,
      // show_group: terminal === 'hem' || terminal === 'hepc' ? '0' : '1',
      showSkuList,
      ...reqParams,
      ...params
    }
    if (siteName === 'yitahome') {
      obj.show_group = '1'
    }
    addCart(obj)
      .then((res) => {
        // HE 加购弹框提示
        if (siteName === 'hernest') {
          dispatch(updateCurrentAddCartCount(obj.quantity))
          dispatch(updateCurrentAddCartSkuIds([obj.sku_id]))
        }
        //加入失败要弹出提示
        // await mutation()
        useLoading(false)

        // 当参数 show_group='1' 显示分组数据时，调用 setShopGroupList 更新购物车数据，反之调用 setList
        if (obj.show_group === '1') {
          handler.dispatch(setShopGroupList(res))
        } else {
          handler.dispatch(setList(res))
        }
        handler.dispatch(setShopListTotalInfo(res))

        !noDrawer && handler.setOpen(true)
        // message.success('SUCCESS')
        callback?.()
      })
      .catch(() => {
        useLoading(false)
      })
      .finally(() => {
        dispatchGlobalEvent('shopping-finish')
      })

    switch (siteName) {
      case 'yitahome':
        winPixel.addToCart(
          pageName,
          data,
          ref.current.value,
          [
            'AW-557240052/OBYOCNTHscsYEPSd24kC',
            'AW-11323451663/gpMZCLSVq9wYEI_SuJcq',
            'AW-11327320617/QCYvCN78qPIYEKnkpJkq'
          ],
          winParams
        )
        break

      case 'hernest':
        heWinPixel.addToCart(pageName, data, ref.current.value, winParams)
        break
    }

    isPreOrder ? loTrack('Pre Order') : loTrack('ACT')
  }

  const onBuyNow = () => {
    buyNow({
      products_id: data.productsId,
      sku_id: data.skuId,
      quantity: ref.current.value
    })
      .then(() => {
        useLoading(false)
        navigate(routesPath.checkout)
      })
      .catch(() => {
        useLoading(false)
      })
      .finally(() => {
        dispatchGlobalEvent('shopping-finish')
      })

    switch (siteName) {
      case 'yitahome':
        winPixel.addToCart(
          'ProductDetailBuyNow',
          data,
          ref.current.value,
          [
            'AW-557240052/OBYOCNTHscsYEPSd24kC',
            'AW-11323451663/gpMZCLSVq9wYEI_SuJcq',
            'AW-11327320617/QCYvCN78qPIYEKnkpJkq'
          ],
          {
            page: pathnameToPage(location.pathname),
            module: 'FirstScreenBuyNow'
          }
        )
        winPixel.beginCheckout(
          data?.usPrice * ref.current.value,
          [data],
          [
            'AW-557240052/Jy8DCNrznOoYEPSd24kC',
            'AW-11323451663/4lVSCLeVq9wYEI_SuJcq'
          ]
        )
        break

      case 'hernest':
        heWinPixel.addToCart('ProductDetailBuyNow', data, ref.current.value, {
          page: pathnameToPage(location.pathname),
          module: 'FirstScreenBuyNow'
        })
        heWinPixel.beginCheckout(data?.usPrice * ref.current.value, [data])
        break
    }
  }

  React.useEffect(() => {
    ref.current.value = 1
  }, [data?.skuId])

  const refctx = ctx.useRef()

  const { info, pageData, skuData, isActive } = refctx?.current || {}

  const isPreOrderStyle = isHomeProject
    ? isPreOrder
    : isPreOrder && Type.isEmpty(pageData?.activityInfo)

  ctx.useHandle('cart', { onAddCart: onClick, isSoldOut, onBuyNow })

  useGlobalEvent('shopping', () => {
    useLoading(true)
  })

  return {
    ref,
    isActive,
    info,
    pageData,
    loading,
    isPreOrderStyle,
    isPreOrder,
    isSoldOut,
    onClick,
    onBuyNow,
    drawer: <CartDrawer {...handler} />
  }
}
